import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    fetchJobOpenings,
    selectJobOpenings,
    selectFetchOpeningsStatus,
    selectError
} from '../_store/jobpostingsSlice';
import Powered_by_logo from "../assets/images/Powered_by_logo.png";
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";

export const JobOpenings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const jobOpenings = useSelector(selectJobOpenings);
    const fetchStatus = useSelector(selectFetchOpeningsStatus);
    const error = useSelector(selectError);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedJob, setSelectedJob] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [jobToApply, setJobToApply] = useState(null);
    const jobsPerPage = 10;
    
    useEffect(() => {
        dispatch(fetchJobOpenings({ 
            status: 'approved',
            limit: 100 
        }));
    }, [dispatch]);
    
    // Helper function to check if job is recent (within last 7 days)
    const isRecentJob = useCallback((createdAt) => {
        if (!createdAt) return false;
        const jobDate = new Date(createdAt);
        const now = new Date();
        const diffTime = Math.abs(now - jobDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
    }, []);

    const isJobClosed = useCallback((job) => {
    return job?.status === 'closed';
}, []);
    
    // Memoized unique locations for filter dropdown
    const locations = useMemo(() => {
        return [...new Set(jobOpenings.map(job => job.location).filter(Boolean))];
    }, [jobOpenings]);
    
    // Memoized filtered and sorted jobs for performance
    const filteredAndSortedJobs = useMemo(() => {
        let result = [...jobOpenings];
        
        // Apply search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(job => 
                job.name?.toLowerCase().includes(searchLower) ||
                job.enhancedDescription?.toLowerCase().includes(searchLower) ||
                job.originalDescription?.toLowerCase().includes(searchLower)
            );
        }
        
        // Apply location filter
        if (locationFilter) {
            const locationLower = locationFilter.toLowerCase();
            result = result.filter(job => 
                job.location?.toLowerCase().includes(locationLower)
            );
        }
        
        // Apply sorting
        result.sort((a, b) => {
            switch(sortBy) {
                case 'newest':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'oldest':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'name':
                    return (a.name || '').localeCompare(b.name || '');
                default:
                    return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });
        
        return result;
    }, [jobOpenings, searchTerm, locationFilter, sortBy]);
    
    // Pagination
    const { currentJobs, totalPages, indexOfFirstJob, indexOfLastJob } = useMemo(() => {
        const indexOfLast = currentPage * jobsPerPage;
        const indexOfFirst = indexOfLast - jobsPerPage;
        const current = filteredAndSortedJobs.slice(indexOfFirst, indexOfLast);
        const total = Math.ceil(filteredAndSortedJobs.length / jobsPerPage);
        
        return {
            currentJobs: current,
            totalPages: total,
            indexOfFirstJob: indexOfFirst,
            indexOfLastJob: indexOfLast
        };
    }, [filteredAndSortedJobs, currentPage, jobsPerPage]);
    
    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);
    
    const handleViewDetails = useCallback((job) => {
        setSelectedJob(job);
        setShowModal(true);
    }, []);
    
    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setSelectedJob(null);
    }, []);
    
    const handleApplyNow = useCallback((job) => {
        // Handle nested $oid format
        let jobId = job._id || job.id;
        
        if (typeof jobId === 'object' && jobId.$oid) {
            jobId = jobId.$oid;
        }
        
        // Clear any old pending job first
        localStorage.removeItem('pendingJobId');
        
        // Store the new one
        localStorage.setItem('pendingJobId', jobId);
        console.log("Storing job ID:", jobId);
        
        setJobToApply(job);
        setShowLoginPopup(true);
    }, []);
    
    const handleCloseLoginPopup = useCallback(() => {
        setShowLoginPopup(false);
        setJobToApply(null);
    }, []);
    
    const handleRedirectToLogin = useCallback(() => {
        setShowLoginPopup(false);
        navigate('/account/login?role=provider');
    }, [navigate]);
    
    const handleClearFilters = useCallback(() => {
        setSearchTerm('');
        setLocationFilter('');
        setSortBy('newest');
        setCurrentPage(1);
    }, []);
    
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    }, []);
    
    const styles = {
        container: {
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: '2rem'
        },
        headerContainer: {
            textAlign: 'center',
            marginBottom: '2rem'
        },
        header: {
            margin: 0,
            fontSize: '2rem',
            color: '#333'
        },
        subheader: {
            color: '#666',
            fontSize: '1rem',
            marginTop: '0.5rem'
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
            maxWidth: '1200px',
            margin: '0 auto 2rem auto'
        },
        backButton: {
            background: '#6c757d',
            border: 'none',
            color: 'white',
            fontSize: '0.9rem',
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            flex: 1,
            maxWidth: '200px'
        },
        postJobButton: {
            background: '#28a745',
            border: 'none',
            color: 'white',
            fontSize: '0.9rem',
            cursor: 'pointer',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            flex: 1,
            maxWidth: '200px'
        },
        filtersContainer: {
            maxWidth: '1200px',
            margin: '0 auto 2rem auto',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap'
        },
        searchInput: {
            flex: 2,
            minWidth: '250px',
            padding: '12px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '1rem',
            boxSizing: 'border-box'
        },
        locationSelect: {
            flex: 1,
            minWidth: '200px',
            padding: '12px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '1rem',
            backgroundColor: 'white',
            cursor: 'pointer'
        },
        sortSelect: {
            flex: 0.5,
            minWidth: '150px',
            padding: '12px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '1rem',
            backgroundColor: 'white',
            cursor: 'pointer'
        },
        clearFiltersButton: {
            padding: '12px 20px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem'
        },
        jobGrid: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1.5rem'
        },
        jobCard: {
            background: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        },
        jobTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
        },
        newBadge: {
            background: '#28a745',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            animation: 'pulse 1s ease-in-out'
        },
        closedBadge: {
    background: '#6c757d',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
},
disabledApplyButton: {
    background: '#cccccc',
    color: '#666',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontSize: '0.85rem',
    fontWeight: '500'
},
        jobLocation: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#666',
            fontSize: '0.9rem',
            marginBottom: '1rem'
        },
        locationIcon: {
            fontSize: '1rem'
        },
        jobDescription: {
            color: '#555',
            lineHeight: '1.6',
            fontSize: '0.95rem',
            marginBottom: '1rem',
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        jobMeta: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 'auto',
            paddingTop: '1rem',
            borderTop: '1px solid #eee',
            fontSize: '0.85rem',
            color: '#999'
        },
        postedDate: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
        },
        actionButtons: {
            display: 'flex',
            gap: '0.5rem'
        },
        viewButton: {
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '500'
        },
        applyButton: {
            background: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '500'
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '3rem',
            color: '#666',
            fontSize: '1.2rem'
        },
        errorContainer: {
            maxWidth: '600px',
            margin: '2rem auto',
            padding: '2rem',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            textAlign: 'center'
        },
        noJobsContainer: {
            textAlign: 'center',
            padding: '3rem',
            background: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            margin: '2rem auto'
        },
        noJobsIcon: {
            fontSize: '4rem',
            marginBottom: '1rem'
        },
        noJobsTitle: {
            fontSize: '1.5rem',
            color: '#333',
            marginBottom: '1rem'
        },
        noJobsText: {
            color: '#666',
            marginBottom: '1.5rem'
        },
        pagination: {
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '2rem',
            flexWrap: 'wrap'
        },
        pageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            background: 'white',
            color: '#333',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            minWidth: '40px'
        },
        activePageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #007bff',
            background: '#007bff',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            minWidth: '40px'
        },
        resultsCount: {
            maxWidth: '1200px',
            margin: '0 auto 1rem auto',
            color: '#666',
            fontSize: '0.9rem'
        },
        poweredBy: {
            textAlign: 'center',
            marginTop: '3rem'
        },
        poweredByText: {
            marginBottom: '0.5rem',
            fontSize: '0.8rem',
            color: '#666'
        },
        // Modal Styles
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '1rem'
        },
        modalContent: {
            background: 'white',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        },
        modalHeader: {
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        },
        modalTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: 0
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#999',
            padding: '0.5rem',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'all 0.2s'
        },
        modalBody: {
            padding: '1.5rem'
        },
        modalSection: {
            marginBottom: '1.5rem'
        },
        modalLabel: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '0.5rem',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        modalLocation: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#666',
            fontSize: '1rem',
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: '8px'
        },
        modalDescription: {
            color: '#333',
            lineHeight: '1.8',
            fontSize: '1rem',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
        },
        modalFooter: {
            padding: '1.5rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem'
        },
        modalApplyButton: {
            background: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            transition: 'background 0.2s'
        },
        modalCloseButton: {
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            transition: 'background 0.2s'
        },
        // Login Popup Styles
        loginPopupOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
            padding: '1rem'
        },
        loginPopupContent: {
            background: 'white',
            borderRadius: '12px',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            animation: 'slideIn 0.3s ease-out'
        },
        loginPopupHeader: {
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        loginPopupTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#333',
            margin: 0
        },
        loginPopupBody: {
            padding: '2rem',
            textAlign: 'center'
        },
        loginPopupIcon: {
            fontSize: '3rem',
            marginBottom: '1rem'
        },
        loginPopupMessage: {
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
        },
        loginPopupFooter: {
            padding: '1.5rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem'
        },
        loginButton: {
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            flex: 1
        },
        cancelButton: {
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            flex: 1
        }
    };
    
    // Add animation keyframes
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.textContent = `
            @keyframes slideIn {
                from {
                    opacity: 0;
                    transform: translateY(-20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            @keyframes pulse {
                0% {
                    transform: scale(1);
                    opacity: 1;
                }
                50% {
                    transform: scale(1.05);
                    opacity: 0.8;
                }
                100% {
                    transform: scale(1);
                    opacity: 1;
                }
            }
        `;
        document.head.appendChild(styleSheet);
        
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);
    
    // Loading State
    if (fetchStatus === 'loading') {
        return (
            <div style={styles.container}>
                <div style={styles.headerContainer}>
                    <h1 style={styles.header}>Project Openings</h1>
                    <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '180px', height: 'auto', marginTop: '1rem' }} />
                </div>
                <div style={styles.loadingContainer}>
                    <p>Loading Project openings...</p>
                </div>
            </div>
        );
    }
    
    // Error State
    if (fetchStatus === 'failed') {
        return (
            <div style={styles.container}>
                <div style={styles.headerContainer}>
                    <h1 style={styles.header}>Project Openings</h1>
                    <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '180px', height: 'auto', marginTop: '1rem' }} />
                </div>
                <div style={styles.errorContainer}>
                    <h3>Error Loading Projects</h3>
                    <p>{error || 'Failed to load project openings. Please try again later.'}</p>
                    <button 
                        onClick={() => dispatch(fetchJobOpenings({ status: 'approved' }))}
                        style={{...styles.viewButton, marginTop: '1rem'}}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div style={styles.container}>
            <div style={styles.headerContainer}>
                <h1 style={styles.header}>Project Openings</h1>
                <p style={styles.subheader}>Find your next opportunity in the drone industry</p>
                <img src={UAVSphere_logo} alt="UAVSphere Logo" style={{ width: '180px', height: 'auto', marginTop: '1rem' }} />
            </div>
            
            <div style={styles.buttonGroup}>
                <button style={styles.backButton} onClick={() => navigate('/')}>
                    ← Back to Home
                </button>
                <button style={styles.postJobButton} onClick={() => navigate('/jobpostings')}>
                    + Post a Drone Project
                </button>
            </div>
            
            {/* Filters with Sorting */}
            <div style={styles.filtersContainer}>
                <input
                    type="text"
                    placeholder="Search projects by title or description..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    style={styles.searchInput}
                />
                
                <select
                    value={locationFilter}
                    onChange={(e) => {
                        setLocationFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                    style={styles.locationSelect}
                >
                    <option value="">All Locations</option>
                    {locations.map((location, index) => (
                        <option key={index} value={location}>{location}</option>
                    ))}
                </select>
                
                <select
                    value={sortBy}
                    onChange={(e) => {
                        setSortBy(e.target.value);
                        setCurrentPage(1);
                    }}
                    style={styles.sortSelect}
                >
                    <option value="newest">📅 Newest First</option>
                    <option value="oldest">📅 Oldest First</option>
                    <option value="name">🔤 Sort by Name</option>
                </select>
                
                {(searchTerm || locationFilter || sortBy !== 'newest') && (
                    <button
                        onClick={handleClearFilters}
                        style={styles.clearFiltersButton}
                    >
                        Clear All Filters
                    </button>
                )}
            </div>
            
            {/* Results Count with Sort Info */}
            {filteredAndSortedJobs.length > 0 && (
                <div style={styles.resultsCount}>
                    Showing {indexOfFirstJob + 1}-{Math.min(indexOfLastJob, filteredAndSortedJobs.length)} of {filteredAndSortedJobs.length} jobs
                    {sortBy === 'newest' && <span> (Sorted: Newest First)</span>}
                    {sortBy === 'oldest' && <span> (Sorted: Oldest First)</span>}
                    {sortBy === 'name' && <span> (Sorted: By Name)</span>}
                </div>
            )}
            
            {/* Job Cards */}
            {filteredAndSortedJobs.length === 0 ? (
                <div style={styles.noJobsContainer}>
                    <div style={styles.noJobsIcon}>🔍</div>
                    <h3 style={styles.noJobsTitle}>No Projects Found</h3>
                    <p style={styles.noJobsText}>
                        {searchTerm || locationFilter || sortBy !== 'newest'
                            ? 'Try adjusting your search filters or clearing all filters'
                            : 'There are no Project openings at the moment. Check back soon!'}
                    </p>
                    <button 
                        onClick={() => navigate('/jobpostings')}
                        style={styles.postJobButton}
                    >
                        Post a Drone Project
                    </button>
                </div>
            ) : (
                <>
                    <div style={styles.jobGrid}>
                        {currentJobs.map((job, index) => (
                            <div 
                                key={job._id || job.id || index} 
                                style={styles.jobCard}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                }}
                            >
                                <h3 style={styles.jobTitle}>
    {job.name || 'Untitled Position'}
    {isJobClosed(job) ? (
        <span style={styles.closedBadge}>CLOSED</span>
    ) : (
        isRecentJob(job.createdAt) && (
            <span style={styles.newBadge}>NEW</span>
        )
    )}
</h3>
                                
                                <div style={styles.jobLocation}>
                                    <strong>📍 {job.location || 'Location Not Specified'}</strong>
                                </div>
                                
                                <p style={styles.jobDescription}>
                                    {job.enhancedDescription || job.originalDescription || 'No description available'}
                                </p>
                                
                                <div style={styles.jobMeta}>
                                    <span style={styles.postedDate}>
                                        📅 Posted: {formatDate(job.createdAt)}
                                    </span>
                                    <div style={styles.actionButtons}>
                                        <button 
                                            style={styles.viewButton}
                                            onClick={() => handleViewDetails(job)}
                                        >
                                            View Details
                                        </button>
                                        {isJobClosed(job) ? (
    <button 
        style={styles.disabledApplyButton}
        disabled
        title="No longer accepting applications"
    >
        Apply Now
    </button>
) : (
    <button 
        style={styles.applyButton}
        onClick={() => handleApplyNow(job)}
    >
        Apply Now
    </button>
)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={styles.pagination}>
                            <button
                                style={styles.pageButton}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                ← Previous
                            </button>
                            
                            {[...Array(totalPages)].map((_, index) => {
                                const pageNumber = index + 1;
                                const isCurrentPage = pageNumber === currentPage;
                                
                                // Show first page, last page, and pages around current page
                                if (
                                    pageNumber === 1 ||
                                    pageNumber === totalPages ||
                                    (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                                ) {
                                    return (
                                        <button
                                            key={pageNumber}
                                            style={isCurrentPage ? styles.activePageButton : styles.pageButton}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                } else if (
                                    pageNumber === currentPage - 3 ||
                                    pageNumber === currentPage + 3
                                ) {
                                    return <span key={pageNumber} style={{ padding: '0.5rem' }}>...</span>;
                                }
                                return null;
                            })}
                            
                            <button
                                style={styles.pageButton}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </>
            )}
            
            {/* Job Details Modal */}
            {showModal && selectedJob && (
                <div style={styles.modalOverlay} onClick={handleCloseModal}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>
    {selectedJob.name || 'Job Details'}
    {isJobClosed(selectedJob) && (
        <span style={{...styles.closedBadge, marginLeft: '0.75rem'}}>CLOSED</span>
    )}
</h2>
                            <button 
                                style={styles.closeButton}
                                onClick={handleCloseModal}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            <div style={styles.modalLocation}>
                                <span>📍 <strong>Location:</strong> {selectedJob.location || 'Not Specified'}</span>
                            </div>
                            
                            {isRecentJob(selectedJob.createdAt) && (
                                <div style={{...styles.modalSection, background: '#e8f5e9', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                                    <span style={{ color: '#28a745', fontWeight: 'bold' }}>✨ New Opportunity - Posted recently!</span>
                                </div>
                            )}
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>Description</div>
                                <div style={styles.modalDescription}>
                                    {selectedJob.enhancedDescription || selectedJob.originalDescription || 'No description available'}
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>Posted Date</div>
                                <div>{formatDate(selectedJob.createdAt)}</div>
                            </div>
                        </div>
                        
                        <div style={styles.modalFooter}>
                            {isJobClosed(selectedJob) ? (
    <button 
        style={styles.disabledApplyButton}
        disabled
        title="No longer accepting applications"
    >
        Apply Now
    </button>
) : (
    <button 
        style={styles.modalApplyButton}
        onClick={() => {
            handleCloseModal();
            let jobId = selectedJob._id || selectedJob.id;
            
            if (typeof jobId === 'object' && jobId.$oid) {
                jobId = jobId.$oid;
            }
            
            localStorage.removeItem('pendingJobId');
            localStorage.setItem('pendingJobId', jobId);
            console.log("Modal - Storing job ID:", jobId);
            
            handleApplyNow(selectedJob);
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#218838'}
        onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
    >
        Apply Now
    </button>
)}
                            <button 
                                style={styles.modalCloseButton}
                                onClick={handleCloseModal}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Login Popup */}
            {showLoginPopup && (
                <div style={styles.loginPopupOverlay} onClick={handleCloseLoginPopup}>
                    <div style={styles.loginPopupContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.loginPopupHeader}>
                            <h3 style={styles.loginPopupTitle}>Login Required</h3>
                            <button 
                                style={styles.closeButton}
                                onClick={handleCloseLoginPopup}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div style={styles.loginPopupBody}>
                            <div style={styles.loginPopupIcon}>🔐</div>
                            <p style={styles.loginPopupMessage}>
                                Kindly login as a provider to apply for this job.
                            </p>
                        </div>
                        
                        <div style={styles.loginPopupFooter}>
                            <button 
                                style={styles.loginButton}
                                onClick={handleRedirectToLogin}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#0056b3'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#007bff'}
                            >
                                Login as Provider
                            </button>
                            <button 
                                style={styles.cancelButton}
                                onClick={handleCloseLoginPopup}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Powered By Section */}
            <div style={styles.poweredBy}>
                <p style={styles.poweredByText}>
                    Powered by GeoWGS84 <br/>
                    <a href="https://www.geowgs84.com/" target="_blank" rel="noopener noreferrer">
                        <img src={Powered_by_logo} alt="Company Logo" style={{ width: '30px', height: 'auto', marginTop: '5px' }} />
                    </a>
                </p>
            </div>
        </div>
    );
};
